import LogoutIcon from '@mui/icons-material/Logout';
import { Grow, MenuItem, Paper, Popper, ClickAwayListener } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowDropdownIcon14pxSvg from '../assets/images/icons/ArrowDropdownIcon14pxSvg';
import ChevronDown10pxSvg from '../assets/images/icons/ChevronDown10pxSvg';
import logo_dark from '../assets/images/logo/CheckIDBioSense_dark.svg';
import logo_light from '../assets/images/logo/CheckIDBioSense_light.svg';
import US from '../assets/images/flag/US.png';
import VN from '../assets/images/flag/VN.png';
import { NavbarLanguageMenuList } from '../components/StyledComponents';
import { StyledLink } from '../components/StyledLink';
import { useAuth } from '../context/AuthContext';
import { changeLanguage } from '../redux/language/reducer';
import { NavBarButton } from './../components/StyledButton';
import i18n from './../n18i';

const NavBar = () => {
   const { t } = useTranslation();
   const anchorRefLanguage = useRef(null);
   const anchorRefSignOut = useRef(null);
   const { profile, isAuthenticated, logout, infoUserSignUp } = useAuth();
   const [isMediumScreen, setIsMediumScreen] = useState(false);
   const [openLanguage, setOpenLanguage] = useState(false);
   const [openSignOut, setOpenSignOut] = useState(false);
   const { pathname } = useLocation();
   const navigate = useNavigate();

   useEffect(() => {
      const handleResize = () => {
         setIsMediumScreen(window.innerWidth >= 630);
      };
      handleResize();
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const handleToggleLanguage = () => {
      setOpenLanguage(!openLanguage);
   };

   const handleToggleSignout = () => {
      setOpenSignOut(!openSignOut);
   };

   const dispatch = useDispatch();

   const handleChangeLanguage = (language) => {
      switch (language) {
         case 'en':
            i18n.changeLanguage('en');
            dispatch(changeLanguage(language));
            localStorage.setItem('i18nextLngId', '2');
            break;
         case 'vi':
            i18n.changeLanguage('vi');
            dispatch(changeLanguage(language));
            localStorage.setItem('i18nextLngId', '1');
            break;
         default:
            break;
      }
      setOpenLanguage(false);
   };

   const handleSignout = async (e) => {
      // e.preventDefault();
      logout();
      // navigate("/login");
   };

   return (
      <div className='container relative z-[999]'>
         <div className='flex justify-between items-center gap-4 max-sm:flex-col py-6'>
            {pathname === '/login' || pathname === '/invitation' ? (
               <div className="h-[50px]" aria-hidden="true" />
            ) : pathname === '/sign-up' ? (
               <div className="flex items-center justify-end w-full min-h-[50px]">
                  {infoUserSignUp.email && (
                     <div
                        ref={anchorRefSignOut}
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={handleToggleSignout}
                     >
                        <div>
                           <p>
                              {infoUserSignUp.signUpFirstName && infoUserSignUp.signUpFirstName}{" "}
                              {infoUserSignUp.signUpLastName && infoUserSignUp.signUpLastName}
                           </p>
                           <p>{infoUserSignUp.email}</p>
                        </div>
                        <ArrowDropdownIcon14pxSvg
                           className={`text-[#404040] transition-transform ${
                              openSignOut ? "rotate-180" : "rotate-0"
                           }`}
                        />
                     </div>
                  )}
               </div>
            ) : (
               <>
                  <StyledLink as={Link} to="/login">
                     {isMediumScreen ? (
                        <img
                           src={logo_light}
                           width={200}
                           height={50}
                           alt="FacialSenseLight"
                           className=""
                        />
                     ) : (
                        <img
                           src={logo_light}
                           width={200}
                           height={50}
                           alt="FacialSenseLight"
                           className=""
                        />
                     )}
                  </StyledLink>

                  <div className="flex items-center gap-4">
                     <NavBarButton
                        disableRipple
                        onClick={handleToggleLanguage}
                        ref={anchorRefLanguage}
                        id="navbar-language-button"
                        aria-controls={openLanguage ? "navbar-language-menu" : undefined}
                        aria-expanded={openLanguage ? "true" : undefined}
                        aria-haspopup="true"
                     >
                        <img
                           src={localStorage.getItem("i18nextLng") === "vi" ? VN : US}
                           alt="flag"
                           className="w-5 h-5 rounded-full object-cover mr-1"
                        />
                        <p className="font-semibold">
                           {localStorage.getItem("i18nextLng") === "en" ? "EN" : "VI"}
                        </p>
                        {openLanguage ? (
                           <ChevronDown10pxSvg className="ml-1 flip-icon duration-500" />
                        ) : (
                           <ChevronDown10pxSvg className="ml-1 duration-500" />
                        )}
                     </NavBarButton>
                  </div>
               </>
            )}
         </div>

         <Popper
            open={openSignOut}
            anchorEl={anchorRefSignOut.current}
            role={undefined}
            placement='bottom-end'
            transition
            disablePortal
            className='z-10'
         >
            {({ TransitionProps, placement }) => (
               <Grow
                  {...TransitionProps}
                  style={{
                     transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                  }}
               >
                  <Paper style={{ borderRadius: '6px', marginTop: '15px' }}>
                     <ClickAwayListener onClickAway={() => setOpenSignOut(false)}>
                        <NavbarLanguageMenuList>
                           <MenuItem onClick={handleSignout}>
                              <div className='flex items-center gap-3'>
                                 <LogoutIcon sx={{ fontSize: '16px' }} />
                                 <p className='ml-1 text-sm'>{t('button.sign-out')}</p>
                              </div>
                           </MenuItem>
                        </NavbarLanguageMenuList>
                     </ClickAwayListener>
                  </Paper>
               </Grow>
            )}
         </Popper>

         <Popper
            open={openLanguage}
            anchorEl={anchorRefLanguage.current}
            role={undefined}
            placement='bottom-end'
            transition
            disablePortal
            className='z-10'
         >
            {({ TransitionProps, placement }) => (
               <Grow
                  {...TransitionProps}
                  style={{
                     transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                  }}
               >
                  <Paper style={{ borderRadius: '6px' }}>
                     <ClickAwayListener onClickAway={() => setOpenLanguage(false)}>
                        <NavbarLanguageMenuList autoFocusItem={openLanguage}>
                           <MenuItem
                              onClick={() => {
                                 handleChangeLanguage('en');
                              }}
                           >
                              <div className='flex items-center gap-2'>
                                 <img src={US} alt="EN" className="w-5 h-5 rounded-full object-cover" />
                                 <span>{t('common.languageEn')}</span>
                              </div>
                           </MenuItem>
                           <MenuItem
                              onClick={() => {
                                 handleChangeLanguage('vi');
                              }}
                           >
                              <div className='flex items-center gap-2'>
                                 <img src={VN} alt="VI" className="w-5 h-5 rounded-full object-cover" />
                                 <span>{t('common.languageVi')}</span>
                              </div>
                           </MenuItem>
                        </NavbarLanguageMenuList>
                     </ClickAwayListener>
                  </Paper>
               </Grow>
            )}
         </Popper>
      </div>
   );
};

export default NavBar;
